import React, { useState } from 'react';
import type { WorkExperience } from '../../models/v2/profile';
import GlassCard from '../ui/v2/GlassCard';
import { RiBriefcaseLine, RiPencilLine, RiDeleteBinLine, RiAddLine } from 'react-icons/ri';
import ProfileSidePanel from './ProfileSidePanel';
import { profileService } from '../../services/profileService';
import { useProfileMutation } from '../../context/ProfileContext';
import { useConfirm } from '../../context/ConfirmContext';

interface Props {
    experiences: WorkExperience[];
    onUpdate?: () => void;
}

interface FormData {
    id?: string;
    position: string;
    company: string;
    employment_type: string;
    location: string;
    start_month: string;
    start_year: string;
    end_month: string;
    end_year: string;
    is_current: boolean;
    description: string;
}

const INITIAL_FORM: FormData = {
    position: '',
    company: '',
    employment_type: 'Full-time',
    location: '',
    start_month: '',
    start_year: '',
    end_month: '',
    end_year: '',
    is_current: false,
    description: ''
};

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

export default function ExperienceSection({ experiences, onUpdate }: Props) {
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    // const [isSaving, setIsSaving] = useState(false); // Handled by hook
    const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Parse existing experience into form data
    const openEdit = (exp: WorkExperience) => {
        const startDate = new Date(exp.start_date);
        const endDate = exp.end_date ? new Date(exp.end_date) : null;

        setFormData({
            id: exp.id,
            position: exp.position,
            company: exp.company,
            employment_type: exp.employment_type || 'Full-time',
            location: exp.location || '',
            start_month: MONTHS[startDate.getMonth()],
            start_year: startDate.getFullYear().toString(),
            end_month: endDate ? MONTHS[endDate.getMonth()] : '',
            end_year: endDate ? endDate.getFullYear().toString() : '',
            is_current: exp.is_current,
            description: exp.description || ''
        });
        setEditingId(exp.id);
        setErrors({});
        setIsPanelOpen(true);
    };

    const openAdd = () => {
        setFormData(INITIAL_FORM);
        setEditingId(null);
        setErrors({});
        setIsPanelOpen(true);
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.position.trim()) newErrors.position = 'Role title is required';
        if (!formData.company.trim()) newErrors.company = 'Company name is required';
        if (!formData.start_month || !formData.start_year) newErrors.start_date = 'Start date is required';

        if (!formData.is_current) {
            if (!formData.end_month || !formData.end_year) {
                newErrors.end_date = 'End date is required if not current';
            } else {
                // Date Validation: Start must be before End
                const startMonthIdx = MONTHS.indexOf(formData.start_month);
                const endMonthIdx = MONTHS.indexOf(formData.end_month);
                const startDate = new Date(parseInt(formData.start_year), startMonthIdx);
                const endDate = new Date(parseInt(formData.end_year), endMonthIdx);

                if (startDate > endDate) {
                    newErrors.end_date = 'End date cannot be before start date';
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const { mutate, isMutating } = useProfileMutation();

    // ... (keep helpers)

    const handleSave = async () => {
        if (!validate()) return;

        // Construct Dates
        const startMonthIdx = MONTHS.indexOf(formData.start_month);
        const startDate = new Date(parseInt(formData.start_year), startMonthIdx, 1).toISOString();

        let endDate = undefined;
        if (!formData.is_current && formData.end_month && formData.end_year) {
            const endMonthIdx = MONTHS.indexOf(formData.end_month);
            endDate = new Date(parseInt(formData.end_year), endMonthIdx, 1).toISOString();
        }

        // Construct Payload
        const payload = {
            position: formData.position,
            company: formData.company,
            location: formData.location,
            start_date: startDate,
            end_date: endDate,
            is_current: formData.is_current,
            employment_type: formData.employment_type,
            description: formData.description
        } as unknown as WorkExperience; // Cast to satisfy diverse requirements of add/update

        if (editingId) {
            mutate(payload, {
                mutationFn: (data: any) => profileService.updateWorkExperience(editingId, data),
                onSuccess: () => {
                    setIsPanelOpen(false);
                    if (onUpdate) onUpdate();
                },
                successMessage: 'Work experience updated successfully'
            });
        } else {
            mutate(payload, {
                mutationFn: (data: any) => profileService.addWorkExperience(data),
                onSuccess: () => {
                    setIsPanelOpen(false);
                    if (onUpdate) onUpdate();
                },
                successMessage: 'Work experience added successfully'
            });
        }
    };

    const { ask } = useConfirm();

    const handleDelete = async (id: string) => {
        const confirmed = await ask({
            title: "Delete Work Experience",
            message: "Are you sure you want to delete this work experience? This action cannot be undone.",
            confirmText: "Delete",
            variant: "danger"
        });

        if (confirmed) {
            mutate(null, {
                mutationFn: () => profileService.deleteWorkExperience(id),
                onSuccess: () => {
                    if (onUpdate) onUpdate();
                },
                successMessage: 'Work experience deleted'
            });
        }
    };

    return (
        <GlassCard>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-white">Work Experience</h2>
                <button
                    onClick={openAdd}
                    className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                >
                    <RiAddLine size={20} />
                </button>
            </div>

            <div className="space-y-4">
                {experiences.length === 0 ? (
                    <p className="text-gray-500 text-sm">No work experience added.</p>
                ) : (
                    experiences.map((exp) => (
                        <div key={exp.id} className="p-4 rounded-xl bg-black/20 border border-white/5 group hover:border-white/10 transition-colors">
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 shrink-0">
                                        <RiBriefcaseLine className="text-lg" />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-semibold text-white">
                                            {exp.position} - {exp.company}
                                        </h3>
                                        {[
                                            exp.location,
                                            (
                                                <span>
                                                    {exp.start_date ? new Date(exp.start_date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : ''}
                                                    {' - '}
                                                    {exp.is_current ? 'Present' : (exp.end_date ? new Date(exp.end_date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '')}
                                                </span>
                                            ),
                                            exp.employment_type
                                        ].filter(Boolean).map((item, index, arr) => (
                                            <React.Fragment key={index}>
                                                <span>{item}</span>
                                                {index < arr.length - 1 && <span>•</span>}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => openEdit(exp)} className="p-1.5 text-gray-500 hover:text-white transition-colors"><RiPencilLine /></button>
                                    <button onClick={() => handleDelete(exp.id)} className="p-1.5 text-gray-500 hover:text-red-400 transition-colors"><RiDeleteBinLine /></button>
                                </div>
                            </div>

                            {/* Description View */}
                            {exp.description && (
                                <div className="ml-14 mt-3 text-sm text-gray-400 whitespace-pre-wrap leading-relaxed">
                                    {exp.description}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            <ProfileSidePanel
                isOpen={isPanelOpen}
                onClose={() => setIsPanelOpen(false)}
                title={editingId ? 'Edit Work Experience' : 'Add Work Experience'}
                onSave={handleSave}
                isSaving={isMutating}
            >
                {/* Role */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">Role Title <span className="text-red-400">*</span></label>
                    <input
                        type="text"
                        placeholder="e.g. Mobile Application Developer"
                        className={`w-full px-4 py-2.5 rounded-lg bg-black/20 border ${errors.position ? 'border-red-500/50' : 'border-white/10'} focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 text-white placeholder-gray-600 outline-none transition-all`}
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    />
                    {errors.position && <p className="text-xs text-red-400">{errors.position}</p>}
                </div>

                {/* Company */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">Company Name <span className="text-red-400">*</span></label>
                    <input
                        type="text"
                        placeholder="Company name"
                        className={`w-full px-4 py-2.5 rounded-lg bg-black/20 border ${errors.company ? 'border-red-500/50' : 'border-white/10'} focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 text-white placeholder-gray-600 outline-none transition-all`}
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    />
                    {errors.company && <p className="text-xs text-red-400">{errors.company}</p>}
                </div>

                {/* Employment Type */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">Employment Type</label>
                    <select
                        className="w-full px-4 py-2.5 rounded-lg bg-black/20 border border-white/10 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 text-white outline-none transition-all appearance-none"
                        value={formData.employment_type}
                        onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                    >
                        {['Full-time', 'Part-time', 'Contract', 'Internship', 'Freelance'].map(type => (
                            <option key={type} value={type} className="bg-[#0c0e14]">{type}</option>
                        ))}
                    </select>
                </div>

                {/* Location */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">Location</label>
                    <input
                        type="text"
                        placeholder="City, Country or Remote"
                        className="w-full px-4 py-2.5 rounded-lg bg-black/20 border border-white/10 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 text-white placeholder-gray-600 outline-none transition-all"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    />
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 sm:col-span-1 space-y-2">
                        <label className="text-sm font-medium text-gray-300">Start Date <span className="text-red-400">*</span></label>
                        <div className="grid grid-cols-2 gap-2">
                            <select
                                className={`w-full px-2 py-2.5 rounded-lg bg-black/20 border ${errors.start_date ? 'border-red-500/50' : 'border-white/10'} text-white outline-none appearance-none`}
                                value={formData.start_month}
                                onChange={(e) => setFormData({ ...formData, start_month: e.target.value })}
                            >
                                <option value="" className="bg-[#0c0e14]">Month</option>
                                {MONTHS.map(m => <option key={m} value={m} className="bg-[#0c0e14]">{m}</option>)}
                            </select>
                            <input
                                type="number"
                                placeholder="Year"
                                className={`w-full px-3 py-2.5 rounded-lg bg-black/20 border ${errors.start_date ? 'border-red-500/50' : 'border-white/10'} text-white placeholder-gray-600 outline-none`}
                                value={formData.start_year}
                                onChange={(e) => setFormData({ ...formData, start_year: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="col-span-2 sm:col-span-1 space-y-2">
                        <label className="text-sm font-medium text-gray-300">End Date</label>
                        <div className="grid grid-cols-2 gap-2">
                            <select
                                disabled={formData.is_current}
                                className={`w-full px-2 py-2.5 rounded-lg bg-black/20 border ${errors.end_date ? 'border-red-500/50' : 'border-white/10'} text-white outline-none appearance-none disabled:opacity-30`}
                                value={formData.end_month}
                                onChange={(e) => setFormData({ ...formData, end_month: e.target.value })}
                            >
                                <option value="" className="bg-[#0c0e14]">Month</option>
                                {MONTHS.map(m => <option key={m} value={m} className="bg-[#0c0e14]">{m}</option>)}
                            </select>
                            <input
                                type="number"
                                placeholder="Year"
                                disabled={formData.is_current}
                                className={`w-full px-3 py-2.5 rounded-lg bg-black/20 border ${errors.end_date ? 'border-red-500/50' : 'border-white/10'} text-white placeholder-gray-600 outline-none disabled:opacity-30`}
                                value={formData.end_year}
                                onChange={(e) => setFormData({ ...formData, end_year: e.target.value })}
                            />
                        </div>
                        {errors.end_date && <p className="text-xs text-red-400 mt-1">{errors.end_date}</p>}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="is_current"
                        checked={formData.is_current}
                        onChange={(e) => {
                            const checked = e.target.checked;
                            setFormData({
                                ...formData,
                                is_current: checked,
                                end_month: checked ? '' : formData.end_month,
                                end_year: checked ? '' : formData.end_year
                            });
                        }}
                        className="w-4 h-4 rounded border-white/10 bg-black/20 text-blue-500 focus:ring-blue-500/50"
                    />
                    <label htmlFor="is_current" className="text-sm text-gray-300">I currently work here</label>
                </div>

                {/* Description */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">Description / Responsibilities</label>
                    <p className="text-xs text-gray-500 mb-2">Describe your key responsibilities and achievements.</p>
                    <textarea
                        placeholder="e.g. Led a team of 5 developers to ship..."
                        rows={6}
                        className="w-full px-4 py-3 rounded-lg bg-black/20 border border-white/10 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 text-white placeholder-gray-600 outline-none transition-all resize-none leading-relaxed custom-scrollbar"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                </div>

            </ProfileSidePanel>
        </GlassCard>
    );
}
